import React, { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useModal } from 'react-modal-hook';
import classNames from 'classnames';

import {
	SpeakerLayoutTypesBold,
	SpeakerLayoutTypesClassic,
	SpeakerLayoutTypesFun,
} from '../../../../types/template-layouts';
import { ContentEditors, ModuleMap, PageModule, PageModuleType, Speaker } from '../../../../types/working-model';
import { SpeakerDetailsModal } from '../../containers/speaker-details-modal/speaker-details.modal';
import { Button } from '../../../general-ui/button/button';
import { EIcon } from '../../../general-ui/icon/icon';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { ParamsProps } from '../../live-event';
import { getStylingOverrides } from '../../utils';
import SpeakerCard from './speaker-card';
import { getDefaultModuleAlignment, getTextValue } from '../../../../utils/utils';
import RenderEditorDescription from '../../../general-ui/editable-text/render-editor-description';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { useTranslate } from '../../../../i18n/useTranslationModules';
import '../../../../scss/live-event/base/modules/speakers.scss';
import useTranslationRoute from '../../hooks/use-translation-route';


interface SpeakersProps {
	module: PageModule;
	template: string;
	isLandingPage?: boolean;
}

const Speakers = React.forwardRef<HTMLDivElement, SpeakersProps>(({ module, template, isLandingPage }, ref) => {
	const [showMore, setShowMore] = useState(false);
	const params: ParamsProps = useParams();
	const language = params?.language || 'en';
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate([translationRoute, 'homepage']);
	const history = useHistory();
	const [speaker, setSpeaker] = useState<Speaker>();
	const { content, styling_overrides, modules } = module;
	const layout_type = content?.layout_type || "standard";

	const eventBundleContentEditor = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings?.content_editor);
	const workingEventContentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const contentEditor = eventBundleContentEditor || workingEventContentEditor;

	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		backgroundColor,
		backgroundImg,
		titleOverrides,
		customHeadingOverrides,
		descriptionOverrides,
	} = stylingOverrides;

	const maxSpeakers = useMemo(() => {
		switch (layout_type) {
			case SpeakerLayoutTypesClassic.standard:
			case SpeakerLayoutTypesBold.standard:
			case SpeakerLayoutTypesFun.standard:
				return 6;

			case SpeakerLayoutTypesClassic.small_horizontal:
			case SpeakerLayoutTypesBold.small_horizontal:
			case SpeakerLayoutTypesFun.small_horizontal:
				return 4;

			case SpeakerLayoutTypesClassic.staggered:
			case SpeakerLayoutTypesBold.staggered:
			case SpeakerLayoutTypesFun.staggered:
				return 2;

			case SpeakerLayoutTypesClassic.solid:
			case SpeakerLayoutTypesBold.solid:
			case SpeakerLayoutTypesFun.solid:
				return 6;

			case SpeakerLayoutTypesClassic.small_vertical:
			case SpeakerLayoutTypesBold.small_vertical:
			case SpeakerLayoutTypesFun.small_vertical:
				return 4;
			default:
				return 2;
		}
	}, [layout_type]);
	const speakerCount = modules?.length ?? maxSpeakers;
	function limitSpeakersIndex() {
		if (showMore) return speakerCount;
		else return maxSpeakers;
	}
	function handleShowMore() {
		setShowMore((prev) => !prev);
	}

	const [showSpeakerDetailsModal, hideSpeakerDetailsModal] = useModal(
		() => {
			const _relatedSessions = content.related_sessions.find((item: any) => item.speaker === speaker?.speaker);
			return (
				<SpeakerDetailsModal
					template={template}
					onClose={hideSpeakerDetailsModal}
					language={language}
					speaker={speaker as Speaker}
					stylingOverrides={stylingOverrides}
					relatedSessions={_relatedSessions?.sessions ?? []}
					params={params}
					onRelatedSessionClick={history.push}
				/>
			);
		}, [speaker]);

	const { custom_heading, description, title } = getTextValue(content, language);

	return module.is_on && content && (
		<section
			id={ModuleMap[PageModuleType.speakers]}
			className={classNames(
				'speakers',
				`speakers-${layout_type}`,
				template,
				backgroundColor
			)}
			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				<TypographyItem className={classNames('evt-caption-1 speakers-custom_heading', customHeadingOverrides)} tagName="p">
					{t(`speakers.${module.id}.heading`, t('speakers.heading', custom_heading))}
				</TypographyItem>
				<TypographyItem className={classNames('evt-heading-2 speakers-title', titleOverrides)} tagName="h2">
					{t(`speakers.${module.id}.title`, t('speakers.title', title))}
				</TypographyItem>
				<TypographyItem className={classNames("evt-body-text-1 speakers-description", { 'quill-editor': contentEditor === ContentEditors.quill })}>
					<RenderEditorDescription
						descriptionOverrides={descriptionOverrides}
						description={t(`speakers.${module.id}.description`, t('speakers.description', description))}
						useNew={contentEditor === ContentEditors.quill}
						defaultTextAlignment={getDefaultModuleAlignment(module, template)}
					/>
				</TypographyItem>
				<ul className="speakers-list">
					{modules?.slice(0, limitSpeakersIndex()).map((speaker: Speaker) => (
						<SpeakerCard
							onClick={(speaker) => {
								setSpeaker(speaker);
								showSpeakerDetailsModal();
							}}
							key={speaker.speaker}
							speaker={speaker}
							language={language}
							stylingOverrides={stylingOverrides}
							template={template}
							isLandingPage={isLandingPage}
						/>
					))}
				</ul>
				{speakerCount > maxSpeakers && (
					<div className="show-more-wrapper">
						<Button
							classButton="secondary"
							template={template}
							onClick={handleShowMore}
						>
							<EIcon
								className="show-more-speaker-icon"
								name="refresh-transparent"
								position="order-left"
								size="medium"
							/>
							{t("homepage:Show", "")} {showMore ? t('homepage:Less', "") : t('homepage:More', "")}
						</Button>
					</div>
				)}
			</div>
		</section>
	);
});

Speakers.displayName = 'Speakers';

export default Speakers;
